import { useState } from 'react'
import { Box, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import { DataTable, DataTableColumn } from '@components/common/DataTable'
import type { EndpointSort, PerfEndpointRow, PerfTimePoint } from '@api/types/performance'
import { LatencyChart } from './LatencyChart'
import { formatCount, formatMs, formatPercent, latencySeverity } from './format'

interface EndpointsTabProps {
  rows?: PerfEndpointRow[]
  loading: boolean
  error?: string | null
  onRetry: () => void
  sort: EndpointSort
  onSortChange: (sort: EndpointSort) => void
  hours: number
  selected: { route: string; method: string } | null
  onSelect: (selection: { route: string; method: string } | null) => void
  series?: PerfTimePoint[]
  seriesLoading: boolean
  seriesError?: boolean
  onRetrySeries: () => void
}

const SORTS: { value: EndpointSort; label: string }[] = [
  { value: 'total', label: 'Total time consumed' },
  { value: 'p95', label: 'p95 latency' },
  { value: 'p99', label: 'p99 latency' },
  { value: 'avg', label: 'Average latency' },
  { value: 'db', label: 'Database time' },
  { value: 'count', label: 'Request count' },
  { value: 'errors', label: 'Error count' },
]

/**
 * Every endpoint that saw traffic, with a chart for whichever one is selected.
 *
 * Sorted by total time consumed by default rather than by latency. Ranking by latency puts the
 * rarely-called two-second endpoint at the top and buries the forty-millisecond one that runs a
 * million times a day, and the second is almost always the better thing to spend a day on.
 */
export function EndpointsTab({
  rows,
  loading,
  error,
  onRetry,
  sort,
  onSortChange,
  hours,
  selected,
  onSelect,
  series,
  seriesLoading,
  seriesError,
  onRetrySeries,
}: EndpointsTabProps) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const columns: DataTableColumn<PerfEndpointRow>[] = [
    {
      key: 'route',
      header: 'Endpoint',
      primary: true,
      render: (row) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Chip label={row.method} size="small" variant="outlined" sx={{ minWidth: 56 }} />
          <Typography
            variant="body2"
            sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, wordBreak: 'break-all' }}
          >
            {row.route}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'service',
      header: 'Service',
      hideBelow: 'lg',
      render: (row) => (
        <Typography variant="caption" color="text.secondary">
          {row.service}
        </Typography>
      ),
    },
    {
      key: 'count',
      header: 'Calls',
      align: 'right',
      render: (row) => formatCount(row.count),
    },
    {
      key: 'p50',
      header: 'p50',
      align: 'right',
      hideBelow: 'md',
      render: (row) => formatMs(row.p50Ms),
    },
    {
      key: 'p95',
      header: 'p95',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" fontWeight="bold" color={`${latencySeverity(row.p95Ms)}.main`}>
          {formatMs(row.p95Ms)}
        </Typography>
      ),
    },
    {
      key: 'p99',
      header: 'p99',
      align: 'right',
      hideBelow: 'md',
      render: (row) => formatMs(row.p99Ms),
    },
    {
      key: 'max',
      header: 'Max',
      align: 'right',
      hideBelow: 'lg',
      render: (row) => formatMs(row.maxMs),
    },
    {
      key: 'db',
      header: 'DB / req',
      align: 'right',
      hideBelow: 'md',
      // Latency and query count together: 400ms across two queries is a slow query, 400ms across
      // four hundred is an N+1, and the two need completely different fixes.
      render: (row) => (
        <Stack alignItems="flex-end">
          <Typography variant="body2">{formatMs(row.avgDbMs)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.avgQueriesPerRequest.toFixed(1)} queries
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'total',
      header: 'Total time',
      align: 'right',
      hideBelow: 'lg',
      render: (row) => formatMs(row.totalMs),
    },
    {
      key: 'errors',
      header: 'Errors',
      align: 'right',
      render: (row) =>
        row.errorCount === 0 ? (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        ) : (
          <Typography variant="body2" color="error.main" fontWeight="bold">
            {formatCount(row.errorCount)}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {formatPercent(row.errorRatePercent, 1)}
            </Typography>
          </Typography>
        ),
    },
  ]

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <FormControl size="small" sx={{ minWidth: { sm: 220 }, width: { xs: '100%', sm: 'auto' } }}>
          <InputLabel id="endpoint-sort">Rank by</InputLabel>
          <Select
            labelId="endpoint-sort"
            label="Rank by"
            value={sort}
            onChange={(e) => onSortChange(e.target.value as EndpointSort)}
          >
            {SORTS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selected && (
          <Chip
            label={`Charting ${selected.method} ${selected.route}`}
            onDelete={() => onSelect(null)}
            color="primary"
            variant="outlined"
            sx={{ maxWidth: '100%' }}
          />
        )}
      </Stack>

      <LatencyChart
        title={selected ? `${selected.method} ${selected.route}` : 'All endpoints'}
        points={series}
        hours={hours}
        loading={seriesLoading}
        error={seriesError}
        onRetry={onRetrySeries}
        height={260}
      />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Tap a row (or its Chart button) to chart it. Routes are templates, so every id maps to one row.
        </Typography>

        <DataTable
          rows={rows ?? []}
          columns={columns}
          getRowKey={(row) => `${row.service}|${row.method}|${row.route}`}
          loading={loading}
          error={error}
          onRetry={onRetry}
          emptyMessage="No endpoint traffic recorded in this window."
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onRowClick={(row) => onSelect({ route: row.route, method: row.method })}
          // DataTable rows only respond to a pointer; the action is the keyboard-reachable route.
          getRowActions={(row) => [
            {
              key: 'chart',
              label: 'Chart',
              icon: <ShowChartIcon fontSize="small" />,
              hint: `Chart ${row.method} ${row.route}`,
              onClick: () => onSelect({ route: row.route, method: row.method }),
            },
          ]}
        />
      </Box>
    </Stack>
  )
}
